import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread";
import SmsIcon from "@mui/icons-material/Sms";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import { ProposalFieldCategoryType, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

const TabLabelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  gap: "6px",
  marginLeft: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
  "&:hover .status-chip-label-text": {
    marginLeft: theme.spacing(0.375),
    maxWidth: theme.spacing(15),
    opacity: 1,
  },
}));

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "0px",
  flexDirection: "row",
  flexWrap: "wrap",
  "& > .MuiChip-root": {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  height: theme.spacing(2.5),
  fontSize: "0.65rem",
  fontWeight: 600,
  overflow: "hidden",
  transition: theme.transitions.create("max-width", {
    duration: theme.transitions.duration.shortest,
  }),
  "& .MuiChip-icon": {
    fontSize: "0.85rem",
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(-0.25),
  },
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },
}));

const StatusChipLabel = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
  whiteSpace: "nowrap",
}));

const StatusChipLabelText = styled("span")(({ theme }) => ({
  display: "inline-block",
  marginLeft: 0,
  maxWidth: 0,
  opacity: 0,
  overflow: "hidden",
  transition: theme.transitions.create(["margin-left", "max-width", "opacity"], {
    duration: theme.transitions.duration.shortest,
  }),
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: theme.spacing(42),
  padding: theme.spacing(1.25),
}));

const PopoverSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
}));

const PopoverMessage = styled(Typography)(() => ({
  fontSize: "0.75rem",
  lineHeight: 1.35,
}));

const StatusPopper = styled(Popper)(({ theme }) => ({
  pointerEvents: "none",
  zIndex: theme.zIndex.tooltip,
}));

const PopperSurface = styled(Paper)(({ theme }) => ({
  backdropFilter: "blur(10px) brightness(105%)",
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  pointerEvents: "none",
}));

type ProposalIssuesBadgeProps = {
  issues?: ProposalIssues;
  commentThreads?: CommentThread[];
  category: ProposalFieldCategoryType;
  title?: string;
  pendingCommentCount?: number;
};

export default function ProposalIssuesBadge(props: Readonly<ProposalIssuesBadgeProps>) {
  const { issues, commentThreads, category, title, pendingCommentCount = 0 } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closePopoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredThreads = commentThreads?.filter(
    (comment) => comment.field.category === category && comment.field.name !== ProposalFieldName.General,
  );
  const filteredIssues = issues?.entries.filter((issue) => issue.field.category === category);
  const numErrors = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Critical).length ?? 0;
  const numWarnings = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Warning).length ?? 0;
  const numComments = filteredThreads?.reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;

  function getPopoverContent() {
    const criticalIssues = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Critical) ?? [];
    const warningIssues = filteredIssues?.filter((issue) => issue.level === ProposalIssueLevel.Warning) ?? [];
    const unreadThreads = filteredThreads?.filter((thread) => (thread.numUnreadMessages ?? 0) > 0) ?? [];

    if (
      criticalIssues.length === 0 &&
      warningIssues.length === 0 &&
      unreadThreads.length === 0 &&
      pendingCommentCount === 0
    ) {
      return "";
    }

    return (
      <PopoverContent>
        {criticalIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Blockers</Typography>
            {criticalIssues.map((issue) => (
              <PopoverMessage key={issue.field.name + (issue.field.id?.value ?? "")}>
                {issue.description}
              </PopoverMessage>
            ))}
          </PopoverSection>
        )}
        {warningIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Warnings</Typography>
            {warningIssues.map((issue) => (
              <PopoverMessage key={issue.field.name + (issue.field.id?.value ?? "")}>
                {issue.description}
              </PopoverMessage>
            ))}
          </PopoverSection>
        )}
        {(unreadThreads.length > 0 || pendingCommentCount > 0) && (
          <PopoverSection>
            <Typography variant="subtitle2">Comments</Typography>
            {unreadThreads.map((thread, index) => {
              const unreadCount = thread.numUnreadMessages ?? 0;

              return (
                <PopoverMessage
                  key={
                    thread.id?.value ??
                    `${thread.proposalId.value}-${thread.field.cacheKey}-${thread.isExternal}-${thread.numUnreadMessages ?? 0}-${index}`
                  }
                >
                  {unreadCount} new comment{unreadCount > 1 ? "s" : ""}.
                </PopoverMessage>
              );
            })}
            {pendingCommentCount > 0 && (
              <PopoverMessage key={`pending-${category}`}>
                {pendingCommentCount} pending comment{pendingCommentCount > 1 && "s"} not yet shared.
              </PopoverMessage>
            )}
          </PopoverSection>
        )}
      </PopoverContent>
    );
  }

  const popoverContent = getPopoverContent();
  const isPopoverOpen = Boolean(anchorEl) && Boolean(popoverContent);
  const popperOffset = anchorEl?.closest(".Mui-selected") ? 16 : 8;

  useEffect(
    () => () => {
      if (closePopoverTimeout.current) clearTimeout(closePopoverTimeout.current);
    },
    [],
  );

  function clearClosePopoverTimeout() {
    if (!closePopoverTimeout.current) return;

    clearTimeout(closePopoverTimeout.current);
    closePopoverTimeout.current = null;
  }

  function openPopover(event: MouseEvent<HTMLElement>) {
    if (!popoverContent) return;

    clearClosePopoverTimeout();
    setAnchorEl(event.currentTarget);
  }

  function closePopover() {
    clearClosePopoverTimeout();
    closePopoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  }

  return (
    <>
      <TabLabelContainer
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
      >
        <span>{title ?? category.toString()}</span>
        <BadgeContainer>
          {numErrors > 0 && (
            <StatusChip
              aria-label={getStatusLabel(numErrors, "blocker")}
              color="error"
              data-testid="issue-critical-badge"
              icon={<ErrorOutlinedIcon />}
              label={getStatusChipLabel(numErrors, "blocker")}
              size="small"
              variant="outlined"
            />
          )}
          {numWarnings > 0 && (
            <StatusChip
              aria-label={getStatusLabel(numWarnings, "warning")}
              color="warning"
              data-testid="issue-warning-badge"
              icon={<WarningAmberIcon />}
              label={getStatusChipLabel(numWarnings, "warning")}
              size="small"
              variant="outlined"
            />
          )}
          {pendingCommentCount > 0 && (
            <StatusChip
              aria-label={getStatusLabel(pendingCommentCount, "pending comment")}
              color="secondary"
              data-testid="pending-comment-badge"
              icon={<SmsIcon />}
              label={getStatusChipLabel(pendingCommentCount, "pending comment")}
              size="small"
              variant="outlined"
            />
          )}
          {numComments > 0 && (
            <StatusChip
              aria-label={getStatusLabel(numComments, "unread comment")}
              color="secondary"
              data-testid="unread-comment-badge"
              icon={<MarkChatUnreadIcon />}
              label={getStatusChipLabel(numComments, "unread comment")}
              size="small"
              variant="outlined"
            />
          )}
        </BadgeContainer>
      </TabLabelContainer>
      <StatusPopper
        anchorEl={anchorEl}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, popperOffset],
            },
          },
        ]}
        open={isPopoverOpen}
        placement="bottom-start"
      >
        <PopperSurface elevation={0}>{popoverContent}</PopperSurface>
      </StatusPopper>
    </>
  );
}

function getStatusLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return `${count} ${count === 1 ? singularLabel : pluralLabel}`;
}

function getStatusChipLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return (
    <StatusChipLabel>
      <span>{count}</span>
      <StatusChipLabelText className="status-chip-label-text">
        {count === 1 ? singularLabel : pluralLabel}
      </StatusChipLabelText>
    </StatusChipLabel>
  );
}
